import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader, Spinner, Surface, CopyButton } from "@/components/common";
import { RefinePanel } from "@/components/RefinePanel";
import { AttachmentUploader } from "@/components/AttachmentUploader";
import { LanguageSelect } from "@/components/LanguageSelect";
import { useLocale } from "@/context/LocaleContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "idea", label: "Kampagneidé" },
  { key: "main_message", label: "Hovedbudskab" },
  { key: "linkedin_ad", label: "LinkedIn-annonce" },
  { key: "meta_ad", label: "Meta-annonce" },
  { key: "google_ads", label: "Google Ads-tekst" },
  { key: "cta", label: "Call To Action" },
  { key: "landing_page", label: "Landingpage-idé" },
];

export default function Campaigns() {
  const location = useLocation();
  const { t, contentLang } = useLocale();
  const [goal, setGoal] = useState("");
  const [result, setResult] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [language, setLanguage] = useState(contentLang);

  useEffect(() => {
    if (location.state?.topic) { setGoal(location.state.topic); generate(location.state.topic); }
    // eslint-disable-next-line
  }, []);

  const generate = async (arg) => {
    const value = typeof arg === "string" ? arg : goal;
    if (!value.trim()) return toast.error(t("Beskriv kampagnens mål"));
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/campaign", { goal: value, language, attachment_ids: attachments.map((a) => a.id) });
      setResult(data.result);
      setProjectId(data.project_id);
      toast.success(t("Kampagne klar — gemt automatisk"));
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };

  const allText = result ? FIELDS.map((f) => `${t(f.label)}:\n${result[f.key]}`).join("\n\n") : "";

  return (
    <div>
      <PageHeader overline="Kampagner" title="Fra mål til færdig kampagne"
        subtitle='Beskriv målet — fx "Vi vil have flere leads" — så bygger vi hele kampagnen.' />

      <div className="space-y-3 mb-8">
        <Textarea data-testid="campaign-goal" value={goal} onChange={(e) => setGoal(e.target.value)}
          rows={3} placeholder={t("Hvad er kampagnens mål?")} />
        <AttachmentUploader files={attachments} onChange={setAttachments} disabled={loading} />
        <Button onClick={() => generate()} disabled={loading} className="gap-2" data-testid="campaign-generate-btn">
          <Sparkles className="h-4 w-4" /> {t("Byg kampagne")}
        </Button>
      </div>

      {loading && <Spinner label="Bygger jeres kampagne..." />}
      {result && (
        <div data-testid="campaign-result">
          <div className="flex justify-end mb-4"><CopyButton text={allText} label="Kopiér hele kampagnen" testid="campaign-copy-all" /></div>
          <div className="space-y-4">
            {FIELDS.map((field, i) => (
              <Surface key={field.key} delay={i * 0.05} testid={`campaign-field-${field.key}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{field.label}</p>
                  <CopyButton text={result[field.key]} testid={`campaign-copy-${field.key}`} label="" />
                </div>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{result[field.key]}</p>
              </Surface>
            ))}
          </div>

          <div className="mt-4">
            <RefinePanel result={result} setResult={setResult} module="campaign" projectId={projectId} language={language}
              quick={["Gør det mere konkret", "Skarpere hovedbudskab", "Mere salgsorienteret", "Kortere annoncetekster", "Prøv en anden vinkel"].map((q) => t(q))} />
          </div>
        </div>
      )}
    </div>
  );
}
